import React, { useState, useEffect } from 'react';
import { ArrowLeft, X, CheckCircle, Clock, HelpCircle, Download, CircleX } from 'lucide-react';
import './paymentSuccess.css';
import { StorageServices } from 'utils';
import { getPaymentDetailsApi } from '_services';

const PaymentSuccessOffcanvas = ({
    isOpen,
    onClose,
    onDownloadReceipt,
    onContactUs,
    details
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch payment details
    useEffect(() => {
        if (isOpen) {
            fetchPaymentDetails(isOpen);
        }
    }, [isOpen]);

    const fetchPaymentDetails = async (isOpen) => {
        setLoading(true);

        if (isOpen) {
            setPaymentDetails(isOpen);

            // Set step based on transaction status
            if (isOpen?.approval_status === 5) {
                setCurrentStep(3); // Directly set to completed
            } else if (isOpen?.approval_status === 9 || isOpen?.approval_status === 4) {
                setCurrentStep(4); // Failed
            } else {
                setCurrentStep(1); // Initiated
            }
        }
        setLoading(false);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Extract payment data from API response
    const getPaymentData = () => {
        if (!paymentDetails) return null;

        const convenienceFee = 5; // Default or calculate based on your logic
        const additionalFee = 5;
        const walletDeducted = 2;

        return {
            amount: paymentDetails?.total_amount || 0,
            transactionId: paymentDetails?.transaction_no || 'N/A',
            date: formatDate(paymentDetails?.date || paymentDetails?.createdAt),
            bankRef: paymentDetails?.eb_transaction_reference
                ? `${paymentDetails?.vendor_bank_account?.bank_name || 'Bank'} - ${paymentDetails?.eb_transaction_reference}`
                : 'N/A',
            paymentMode: paymentDetails?.payment_mode?.name || 'N/A',
            paymentAmount: paymentDetails?.total_amount || 0,
            convenienceFee: convenienceFee,
            additionalFee: additionalFee,
            walletDeducted: walletDeducted,
            fromAccount: paymentDetails?.from_account?.name || 'N/A',
            accountNumber: paymentDetails?.vendor_bank_account?.account_number || 'N/A',
            toAccountName: paymentDetails?.vendor_bank_account?.account_name || paymentDetails?.vendor_detail?.name || 'N/A',
            bankName: paymentDetails?.vendor_bank_account?.bank_name || 'N/A',
            ifscCode: paymentDetails?.vendor_bank_account?.ifsc_code || 'N/A',
            category: paymentDetails?.categories?.[0]?.category?.name || paymentDetails?.sub_type_name || 'N/A',
            description: paymentDetails?.description || 'Non Mentioned',
            tripNumber: paymentDetails?.tags?.vehicle?.vehicle_no || 'N/A',
            voucherNo: paymentDetails?.voucher_no || 'N/A',
            panNo: paymentDetails?.vendor_detail?.pan_no || 'N/A',
            ebRequestNumber: paymentDetails?.eb_request_number || 'N/A'
        };
    };

    const paymentData = getPaymentData();

    // Show loading state
    if (loading || !paymentData) {
        return (
            <div>
                <div
                    className={`offcanvas offcanvas-end common_offCanvas ${isOpen ? "show" : ""}`}
                    tabIndex={-1}
                    style={{ visibility: isOpen ? "visible" : "hidden" }}
                >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <p>Loading payment details...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div
                className={`offcanvas offcanvas-end common_offCanvas ${isOpen ? "show" : ""}`}
                tabIndex={-1}
                style={{
                    visibility: isOpen ? "visible" : "hidden",
                }}
            >
                {isOpen && (
                    <>
                        {/* Header */}
                        <div className="offcanvas-header offcan_header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '0',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    onClick={onClose}
                                >
                                    <ArrowLeft size={20} color="#000" />
                                </button>
                                <h5 className="offcanvas-title">Confirm Payment</h5>
                            </div>

                            <button
                                type="button"
                                style={{
                                    marginLeft: 'auto',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={onClose}
                            >
                                <X size={18} color="#000" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="offcanvas-body " style={{ background: '#F5F8FD', padding: '9px 14px' }}>
                            <div className='__sucMain_fxs'>
                                <div className='__succesBxs'>
                                    <div className="success-banner">
                                        <div className={`success-icon ${currentStep === 4 ? "Failed" : currentStep === 3 ? 'completed' : ''}`}>
                                            {currentStep === 4 ? (
                                                <X size={48} color="#fff" strokeWidth={2.5} />
                                            ) : currentStep === 3 ? (
                                                <CheckCircle size={32} color="#10b981" strokeWidth={2.5} />
                                            ) : (
                                                <Clock size={32} color="#f59e0b" strokeWidth={2.5} />
                                            )}
                                        </div>
                                        <p className="success-status">
                                            {currentStep === 1 && 'Payment Initiated'}
                                            {currentStep === 2 && 'Transfer in Progress'}
                                            {currentStep === 3 && 'Payment Completed'}
                                            {currentStep === 4 && 'Payment Failed'}
                                        </p>
                                        <h2 className="success-amount">₹{paymentData.amount}</h2>
                                        <p className="success-message">
                                            {currentStep === 1 && (
                                                <>Payment initiated successfully. Awaiting bank confirmation</>
                                            )}
                                            {currentStep === 2 && (
                                                <>Bank is processing your transfer. Please wait...</>
                                            )}
                                            {currentStep === 3 && (
                                                <>Payment completed successfully. Thank you for using our service!</>
                                            )}
                                            {currentStep === 4 && (
                                                <>Payment Failed. Please try again or contact support.</>
                                            )}
                                        </p>
                                    </div>

                                    {/* Status Steps - Vertical Timeline Design */}
                                    <div className='setp-container'>
                                        <div className="status-steps">
                                            {/* Step 1: Payment Initiated */}
                                            <div className={`status-step ${currentStep >= 1 ? 'completed' : 'pending'}`}>
                                                <div className="step-timeline">
                                                    <div className={`step-icon ${currentStep >= 1 ? 'completed' : 'pending'}`}>
                                                        {currentStep > 2 ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" color='#ffff' width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                        ) : currentStep === 4 ? (
                                                            <X size={24} color="#ef4444" strokeWidth={2.5} />
                                                        ) : (
                                                            <Clock size={24} color="#9ca3af" />
                                                        )}
                                                    </div>
                                                    <div className={`step-line ${currentStep >= 2 ? 'completed' : ''}`}></div>
                                                </div>
                                                <div className="step-content">
                                                    <p className="step-title">Payment Initiated</p>
                                                    <p className="step-subtitle">{paymentData.date}</p>
                                                </div>
                                            </div>

                                            {/* Step 2: Transfer Initiated */}
                                            <div className={`status-step ${currentStep >= 2 ? 'completed' : currentStep === 1 ? 'active' : 'pending'}`}>
                                                <div className="step-timeline">
                                                    <div className={`step-icon ${currentStep >= 2 ? 'completed' : currentStep === 1 ? 'active' : 'pending'}`}>
                                                        {currentStep >= 2 ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" color='#ffff' width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                        ) : currentStep === 4 ? (
                                                            <CircleX size={24} color="#ef4444" strokeWidth={2.5} />
                                                        ) : (
                                                            <Clock size={24} color={currentStep === 1 ? "#f59e0b" : "#9ca3af"} />
                                                        )}
                                                    </div>
                                                    <div className={`step-line ${currentStep >= 3 ? 'completed' : ''}`}></div>
                                                </div>
                                                <div className="step-content">
                                                    <p className="step-title">Transfer Initiated</p>
                                                    {currentStep === 1 && <p className="step-subtitle">Waiting to start...</p>}
                                                    {currentStep === 2 && <p className="step-subtitle">Bank verification in progress</p>}
                                                    {currentStep >= 3 && <p className="step-subtitle">Transfer completed</p>}
                                                </div>
                                            </div>

                                            {/* Step 3: Completion */}
                                            <div className={`status-step ${currentStep >= 3 ? 'completed' : currentStep === 2 ? 'active' : 'pending'}`}>
                                                <div className="step-timeline">
                                                    <div className={`step-icon ${currentStep === 4 ? "Failed" : currentStep === 3 ? 'completed' : currentStep === 2 ? 'active' : 'pending'}`}>
                                                        {currentStep === 3 ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" color='#ffff' width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                        ) : currentStep === 4 ? (
                                                            <X size={24} color="#fff" strokeWidth={2.5} />
                                                        ) : (
                                                            <Clock size={24} color={currentStep === 2 ? "#f59e0b" : "#9ca3af"} />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="step-content">
                                                    <p className="step-title">{currentStep === 4 ? "Payment Failed" : "Completion"}</p>
                                                    {currentStep < 2 && <p className="step-subtitle">Pending confirmation</p>}
                                                    {currentStep === 2 && <p className="step-subtitle">Finalizing payment...</p>}
                                                    {currentStep === 3 && <p className="step-subtitle">Payment completed successfully</p>}
                                                    {currentStep === 4 && <p className="step-subtitle">Transaction failed</p>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='__midLineer'></div>

                                    {/* Transaction Details */}
                                    <div className="transaction-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Transaction ID</span>
                                            <span className="detail-value">{paymentData.transactionId}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Voucher No</span>
                                            <span className="detail-value">{paymentData.voucherNo}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Date</span>
                                            <span className="detail-value">{paymentData.date}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Bank Transfer Ref</span>
                                            <span className="detail-value">{paymentData.bankRef}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">EB Request No</span>
                                            <span className="detail-value">{paymentData.ebRequestNumber}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Payment Mode</span>
                                            <span className="detail-value">{paymentData.paymentMode}</span>
                                        </div>
                                    </div>

                                    <div className='__midLineer'></div>

                                    <div className="transaction-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Payment Amount</span>
                                            <span className="detail-value">₹ {paymentData.paymentAmount}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Convenience Fee</span>
                                            <span className="detail-value">₹ {paymentData.convenienceFee} + ₹ {paymentData.additionalFee}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Wallet Deducted</span>
                                            <span className="detail-value">₹ {paymentData.walletDeducted}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className='__succesBxs'>
                                    <div className="transaction-details">
                                        <div className="detail-row">
                                            <span className="detail-label">From Account</span>
                                            <span className="detail-value">{paymentData.fromAccount}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">To Account</span>
                                            <span className="detail-value">{paymentData.toAccountName}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Bank Name</span>
                                            <span className="detail-value">{paymentData.bankName}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Account Number</span>
                                            <span className="detail-value">{paymentData.accountNumber}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">IFSC Code</span>
                                            <span className="detail-value">{paymentData.ifscCode}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className='__succesBxs'>
                                    <div className="transaction-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Transaction Category</span>
                                            <span className="detail-value">{paymentData.category}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Description</span>
                                            <span className="detail-value">{paymentData.description}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">Vehicle Number</span>
                                            <span className="detail-value">{paymentData.tripNumber}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="detail-label">PAN Number</span>
                                            <span className="detail-value">{paymentData.panNo}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Info Boxes */}
                            <div className="info-boxes">
                                {/* What's Next */}
                                <div className="info-box blue">
                                    <h4 className="info-title bluedark">What's Next?</h4>
                                    <p className="info-text bluedark-text">
                                        {currentStep < 3 ? (
                                            <>Your Payment is being processed by the bank. You'll receive a confirmation notification once the transaction is complete. Estimate time: 30min - 2 hours for NEFT, Instant for IMPS.</>
                                        ) : currentStep === 3 ? (
                                            <>Your payment has been completed successfully. You can download the receipt below for your records.</>
                                        ) : (
                                            <>Your payment could not be processed. Please check your account details and try again or contact support.</>
                                        )}
                                    </p>
                                </div>

                                {/* Money Deduction Info */}
                                <div className="info-box light">
                                    <h4 className="info-title lightdark">Wallet Debited</h4>
                                    <p className="info-text lightdark-text">
                                        ₹ {paymentData.walletDeducted} has been deducted from your wallet as a convenience fee.
                                    </p>
                                </div>

                                {/* Help */}
                                <div className="help-box">
                                    <HelpCircle size={20} color="#3b82f6" />
                                    <div className="help-content">
                                        <p className="help-text">Need help with this transaction?</p>
                                        <button className="contact-link" onClick={onContactUs}>Contact Us</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="offcanvas-footer">
                            <button className="download-btn" onClick={onDownloadReceipt}>
                                <Download size={20} />
                                <span>Download Receipt</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccessOffcanvas;